import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Check, ChevronDown } from "lucide-react";
import ReactCountryFlag from "react-country-flag";
import { LANGUAGE_STORAGE_KEY } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

const LANGUAGES = [
  { code: "en", nameKey: "language.english" as const, countryCode: "GB" },
  { code: "sv", nameKey: "language.swedish" as const, countryCode: "SE" },
] as const;

type LanguageSwitcherProps = {
  className?: string;
  /** Compact trigger (flag + chevron only); default shows a short code label */
  compact?: boolean;
};

export function LanguageSwitcher({
  className,
  compact = false,
}: LanguageSwitcherProps) {
  const { i18n, t } = useTranslation("common");
  const raw = i18n.language || "en";

  const resolvedCode = useMemo(
    () => LANGUAGES.find((l) => raw.startsWith(l.code))?.code ?? "en",
    [raw]
  );

  const current = useMemo(
    () => LANGUAGES.find((l) => l.code === resolvedCode) ?? LANGUAGES[0],
    [resolvedCode]
  );

  const handleChange = useCallback(
    (lng: string) => {
      if (lng === resolvedCode) return;
      void i18n.changeLanguage(lng);
      try {
        window.localStorage.setItem(LANGUAGE_STORAGE_KEY, lng);
      } catch {
        // ignore persistence errors (e.g. private mode)
      }
    },
    [resolvedCode, i18n]
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={cn(
            "h-8 gap-1.5 px-2 font-normal text-foreground hover:bg-accent/80",
            className
          )}
          aria-label={t("language.chooseLanguage")}
        >
          <ReactCountryFlag
            countryCode={current.countryCode}
            svg
            style={{ width: "1.25rem", height: "auto" }}
            aria-hidden
          />
          {!compact && (
            <span className="text-xs font-medium tabular-nums">
              {resolvedCode.toUpperCase()}
            </span>
          )}
          <ChevronDown
            className="h-3.5 w-3.5 shrink-0 opacity-60"
            strokeWidth={2}
            aria-hidden
          />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={6} className="min-w-44">
        {LANGUAGES.map((lang) => {
          const selected = lang.code === resolvedCode;
          return (
            <DropdownMenuItem
              key={lang.code}
              className="gap-2"
              onSelect={() => {
                handleChange(lang.code);
              }}
            >
              <ReactCountryFlag
                countryCode={lang.countryCode}
                svg
                style={{ width: "1.25rem", height: "auto" }}
                aria-hidden
              />
              <span className="flex-1">{t(lang.nameKey)}</span>
              <Check
                className={cn(
                  "h-4 w-4 shrink-0 text-primary",
                  !selected && "invisible"
                )}
                aria-hidden
              />
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
